// Pure indexing over triage issues: which worktree entities an issue touches,
// the per-entity direct index (tree badges, page indicators), the ancestor
// roll-ups (distinct issues per subtree — a cross-diagram issue touching two
// processes counts ONCE at their product), and the per-process BPMN element
// ids (the diagram's Improvements layer dots).
//
// This module is the single choke point every surfacing consumer derives
// from (badges, roll-ups, scoped queue, counts). Keep it that way: when
// entitlement tiers arrive, their filtering slots in here once — and a locked
// issue must never inflate a count.
import type { TriageIssue } from "@/components/Triage/triage-issues";

/** The worktree slice the roll-ups walk (structural — `types.Worktree` fits). */
export type WorktreeHierarchy = {
    domains: Array<{
        identity: string;
        products: Array<{
            identity: string;
            processes: Array<{ identity: string }>;
        }>;
    }>;
};

/**
 * Every entity identity an issue provides evidence against: the processes of
 * a pool issue, a model issue's occurrence/lane processes, and the linked
 * application for application-subject issues (system-lane-on-fake-role).
 */
export const issueEntityIds = (issue: TriageIssue): Set<string> => {
    const ids = new Set<string>();
    if (issue.type === "model-issue") {
        const api = issue.apiIssue;
        for (const occurrence of api.occurrences ?? []) {
            if (occurrence.process?.id) ids.add(occurrence.process.id);
        }
        for (const lane of api.lanes ?? []) {
            if (lane.process?.id) ids.add(lane.process.id);
        }
        if (api.application?.id) ids.add(api.application.id);
    } else {
        for (const process of issue.processes) ids.add(process.id);
    }
    return ids;
};

/** Direct index: entity identity → the issues that touch it. */
export const buildEntityIssueIndex = (
    issues: TriageIssue[]
): Map<string, TriageIssue[]> => {
    const index = new Map<string, TriageIssue[]>();
    for (const issue of issues) {
        for (const id of issueEntityIds(issue)) {
            const list = index.get(id) ?? [];
            list.push(issue);
            index.set(id, list);
        }
    }
    return index;
};

/**
 * Ancestor roll-ups: domain/product identity → the number of DISTINCT issues
 * touching any entity in its subtree. Distinct by issue id, never a sum of
 * child counts — cross-diagram issues span processes.
 */
export const buildRollupCounts = (
    worktree: WorktreeHierarchy | undefined,
    entityIssueIndex: Map<string, TriageIssue[]>
): Map<string, number> => {
    const counts = new Map<string, number>();
    if (!worktree) return counts;
    const distinct = (ids: string[]): number => {
        const issueIds = new Set<string>();
        for (const id of ids) {
            for (const issue of entityIssueIndex.get(id) ?? []) {
                issueIds.add(issue.id);
            }
        }
        return issueIds.size;
    };
    for (const domain of worktree.domains) {
        const domainIds = [domain.identity];
        for (const product of domain.products) {
            const productIds = [
                product.identity,
                ...product.processes.map((process) => process.identity),
            ];
            domainIds.push(...productIds);
            counts.set(product.identity, distinct(productIds));
        }
        counts.set(domain.identity, distinct(domainIds));
    }
    return counts;
};

/**
 * The identities a scope covers: the entity itself plus its worktree
 * descendants. An identity outside the tree (an application) scopes to
 * itself alone.
 */
export const subtreeIds = (
    worktree: WorktreeHierarchy | undefined,
    entityId: string
): Set<string> => {
    for (const domain of worktree?.domains ?? []) {
        if (domain.identity === entityId) {
            return new Set([
                domain.identity,
                ...domain.products.flatMap((product) => [
                    product.identity,
                    ...product.processes.map((process) => process.identity),
                ]),
            ]);
        }
        for (const product of domain.products) {
            if (product.identity === entityId) {
                return new Set([
                    product.identity,
                    ...product.processes.map((process) => process.identity),
                ]);
            }
        }
    }
    return new Set([entityId]);
};

/** Whether an issue's evidence touches the scope's identity set. */
export const issueInScope = (
    issue: TriageIssue,
    scope: ReadonlySet<string>
): boolean => {
    for (const id of issueEntityIds(issue)) {
        if (scope.has(id)) return true;
    }
    return false;
};

/**
 * Per-process BPMN element ids carrying an open model issue — the diagram
 * layer's dot positions. Pool issues carry no element refs and are excluded;
 * they surface through the tree badge and the pool cards instead.
 */
export const buildIssueBpmnIndex = (
    issues: TriageIssue[]
): Map<string, Set<string>> => {
    const index = new Map<string, Set<string>>();
    const add = (processId?: string | null, bpmnId?: string | null) => {
        if (!processId || !bpmnId) return;
        const ids = index.get(processId) ?? new Set<string>();
        ids.add(bpmnId);
        index.set(processId, ids);
    };
    for (const issue of issues) {
        if (issue.type !== "model-issue") continue;
        for (const occurrence of issue.apiIssue.occurrences ?? []) {
            add(occurrence.process?.id, occurrence.bpmn_id);
        }
        for (const lane of issue.apiIssue.lanes ?? []) {
            add(lane.process?.id, lane.lane_bpmn_id);
        }
    }
    return index;
};
